'use client';

import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';
import Image from '../image';
import type { ThematicCardData } from './types';

type Props = {
	card: ThematicCardData;
	aspectClassName: string;
	widthClassName: string;
};

const CARD_CLASS = 'flex flex-col gap-y-stack-s';

export default function ThematicCard({
	card,
	aspectClassName,
	widthClassName,
}: Props): ReactElement {
	const href = card.href?.trim();

	const content = (
		<>
			{(card.image || card.mobileImage) && (
				<div className={cn('w-full overflow-hidden', aspectClassName)}>
					{card.image && (
						<Image
							roughSize="m"
							imagePrimitive={card.image}
							className={cn(
								'h-full w-full object-cover object-center transition-transform duration-500 ease-out motion-reduce:transition-none',
								href && 'md:group-hover:scale-110',
								card.mobileImage && 'hidden md:block'
							)}
						/>
					)}
					{card.mobileImage && (
						<Image
							roughSize="m"
							imagePrimitive={card.mobileImage}
							className="h-full w-full object-cover object-center transition-transform duration-500 ease-out motion-reduce:transition-none md:hidden"
						/>
					)}
				</div>
			)}

			<div className="flex flex-col gap-y-stack-2xs">
				<h3 className="text-h3 line-clamp-2 text-text-heading">
					<BevSafeText>{card.title}</BevSafeText>
				</h3>
				{card.description && (
					<p className="text-body2 line-clamp-2 text-text-primary">
						<BevSafeText>{card.description}</BevSafeText>
					</p>
				)}
			</div>
		</>
	);

	return (
		<li className={cn('group min-w-0', widthClassName)}>
			{href ? (
				<a
					href={href}
					target={card.newWindow ? '_blank' : undefined}
					rel={card.newWindow ? 'noopener' : undefined}
					className={cn(CARD_CLASS, 'focus-visible:outline-2 focus-visible:outline-offset-2')}
				>
					{content}
				</a>
			) : (
				<div className={CARD_CLASS}>{content}</div>
			)}
		</li>
	);
}
